import React, { useState } from 'react';
import { 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Paper,
  IconButton,
  Chip,
  Tooltip,
  Box,
  CircularProgress,
  Typography,
  Avatar,
  TableSortLabel
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import VisibilityIcon from '@mui/icons-material/Visibility';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TrendingDownIcon from '@mui/icons-material/TrendingDown';
import CategoryIcon from '@mui/icons-material/Category';
import BusinessIcon from '@mui/icons-material/Business';
import HealthAndSafetyIcon from '@mui/icons-material/HealthAndSafety';
import ComputerIcon from '@mui/icons-material/Computer';
import SchoolIcon from '@mui/icons-material/School';
import MovieIcon from '@mui/icons-material/Movie';
import FlightIcon from '@mui/icons-material/Flight';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import HomeIcon from '@mui/icons-material/Home';
import NewspaperIcon from '@mui/icons-material/Newspaper';
import SportsBasketballIcon from '@mui/icons-material/SportsBasketball';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SpaIcon from '@mui/icons-material/Spa';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import categoryOptions from '../../../utils/categoryOptions';

const KeywordTable = ({ keywords, loading, onViewStats, onDeleteKeyword, onToggleStatus }) => {
  // State for sorting
  const [orderBy, setOrderBy] = useState('keyword');
  const [order, setOrder] = useState('asc');

  const getStatusColor = (status) => {
    switch(status) {
      case 'active': return 'success';
      case 'paused': return 'warning';
      case 'inactive': return 'error';
      default: return 'default';
    }
  };

  // Get icon for category
  const getCategoryIcon = (category) => {
    const categoryLower = category?.toLowerCase();
    
    // Find the matching category from our shared options
    const categoryOption = categoryOptions.find(option => 
      option.value === categoryLower && !option.header
    );
    
    // If no match is found, return the default category icon
    if (!categoryOption) {
      return <CategoryIcon fontSize="small" />;
    }
    
    // Business & Finance
    if (['business', 'finance', 'investing', 'banking', 'insurance', 'careers'].includes(categoryLower)) {
      return <BusinessIcon fontSize="small" />;
    }
    
    // Technology
    if (['technology', 'computing', 'internet', 'software', 'electronics', 'mobile'].includes(categoryLower)) {
      return <ComputerIcon fontSize="small" />;
    }
    
    // Health & Wellness
    if (['health', 'medicine', 'fitness', 'nutrition', 'mental_health', 'diseases'].includes(categoryLower)) {
      return <HealthAndSafetyIcon fontSize="small" />;
    }
    
    // Lifestyle
    if (['lifestyle', 'fashion', 'beauty', 'home', 'parenting', 'relationships'].includes(categoryLower)) {
      return <SpaIcon fontSize="small" />;
    }
    
    // Entertainment
    if (['entertainment', 'movies', 'music', 'television', 'books', 'gaming', 'celebrities'].includes(categoryLower)) {
      return <MovieIcon fontSize="small" />;
    }
    
    // Travel & Transportation
    if (['travel', 'destinations', 'hotels', 'transportation', 'automotive', 'aviation'].includes(categoryLower)) {
      return <FlightIcon fontSize="small" />;
    }
    
    // Shopping & Retail
    if (['shopping', 'ecommerce', 'consumer_goods', 'luxury', 'discounts'].includes(categoryLower)) {
      return <ShoppingCartIcon fontSize="small" />;
    }
    
    // Real Estate & Property
    if (['real_estate', 'residential', 'commercial', 'rental', 'mortgage'].includes(categoryLower)) {
      return <HomeIcon fontSize="small" />;
    }
    
    // News & Media
    if (['news', 'media', 'journalism', 'current_events', 'politics'].includes(categoryLower)) {
      return <NewspaperIcon fontSize="small" />;
    }
    
    // Sports & Recreation
    if (['sports', 'recreation', 'fitness', 'outdoor', 'games'].includes(categoryLower)) {
      return <SportsBasketballIcon fontSize="small" />;
    }
    
    // Education & Learning
    if (['education', 'learning', 'schools', 'college', 'courses', 'training'].includes(categoryLower)) {
      return <SchoolIcon fontSize="small" />;
    }
    
    // Legal & Government
    if (['legal', 'government', 'law', 'regulations', 'compliance'].includes(categoryLower)) {
      return <AccountBalanceIcon fontSize="small" />;
    }
    
    // Default icon for other categories
    return <CategoryIcon fontSize="small" />;
  };

  // Get stats from keyword - use parent properties directly, with fallback to stats array
  const getKeywordStats = (keyword) => {
    // Check if the parent properties have been populated by the backend
    if (keyword.hits > 0 || keyword.monetized > 0 || keyword.revenue > 0) {
      // Use the parent properties directly
      return {
        hits: keyword.hits || 0,
        monetized: keyword.monetized || 0,
        clicks: keyword.clicks || 0,
        revenue: keyword.revenue || 0,
        coverage: keyword.coverage || 0,
        payout: keyword.payout || 0
      };
    }
    
    // Fallback: Calculate from stats array if parent properties aren't populated
    if (keyword.stats && keyword.stats.length > 0) {
      const totalHits = keyword.stats.reduce((sum, day) => sum + day.hits, 0);
      const totalMonetized = keyword.stats.reduce((sum, day) => sum + day.monetized, 0);
      const totalClicks = keyword.stats.reduce((sum, day) => sum + (day.clicks || 0), 0);
      const totalRevenue = keyword.stats.reduce((sum, day) => sum + day.revenue, 0);
      
      const coverage = totalHits > 0 ? Math.round((totalMonetized / totalHits) * 100) : 0;
      const payout = totalClicks > 0 ? totalRevenue / totalClicks : 0;
      
      return {
        hits: totalHits,
        monetized: totalMonetized,
        clicks: totalClicks,
        revenue: totalRevenue,
        coverage: coverage,
        payout: payout
      };
    }
    
    // Default values if no data is available
    return {
      hits: 0,
      monetized: 0,
      clicks: 0,
      revenue: 0,
      coverage: 0,
      payout: 0
    };
  };

  // Get color for payout
  const getPayoutColor = (payout) => {
    if (payout >= 0.30) return '#2e7d32'; // High payout - Green
    if (payout >= 0.20) return '#1976d2'; // Good payout - Blue
    if (payout >= 0.15) return '#ed6c02'; // Decent payout - Orange
    if (payout >= 0.10) return '#ff9800'; // Average payout - Light Orange
    if (payout > 0) return '#9e9e9e';     // Low but positive - Grey
    return '#f44336';                     // Zero or negative - Red
  };

  // Handle sort request
  const handleRequestSort = (property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  // Create sort handler
  const createSortHandler = (property) => () => {
    handleRequestSort(property);
  };

  // Sort function
  const sortData = (data, comparator) => {
    const stabilizedThis = data.map((el, index) => [el, index]);
    stabilizedThis.sort((a, b) => {
      const order = comparator(a[0], b[0]);
      if (order !== 0) return order;
      return a[1] - b[1];
    });
    return stabilizedThis.map((el) => el[0]);
  };

  // Get comparator
  const getComparator = (order, orderBy) => {
    return order === 'desc'
      ? (a, b) => descendingComparator(a, b, orderBy)
      : (a, b) => -descendingComparator(a, b, orderBy);
  };

  // Descending comparator
  const descendingComparator = (a, b, orderBy) => {
    // Special case for keyword
    if (orderBy === 'keyword') {
      return a.keyword.localeCompare(b.keyword);
    }
    
    // Special case for status
    if (orderBy === 'status') {
      return a.status.localeCompare(b.status);
    }
    
    // Special case for category
    if (orderBy === 'category') {
      const aCategory = a.category || '';
      const bCategory = b.category || '';
      return aCategory.localeCompare(bCategory);
    }
    
    // Special cases for stats
    if (['hits', 'monetized', 'clicks', 'revenue', 'coverage', 'payout'].includes(orderBy)) {
      const statsA = getKeywordStats(a);
      const statsB = getKeywordStats(b);
      return statsB[orderBy] - statsA[orderBy];
    }
    
    return 0;
  };

  // Sort the keywords
  const sortedKeywords = sortData(keywords, getComparator(order, orderBy));

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <TableContainer component={Paper} variant="outlined" sx={{ borderRadius: '12px' }}>
      <Table size="medium">
        <TableHead sx={{ backgroundColor: 'rgba(0, 0, 0, 0.04)' }}>
          <TableRow>
            <TableCell sx={{ fontWeight: 'bold' }}>
              <TableSortLabel
                active={orderBy === 'keyword'}
                direction={orderBy === 'keyword' ? order : 'asc'}
                onClick={createSortHandler('keyword')}
              >
                Keyword
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>
              <TableSortLabel
                active={orderBy === 'status'}
                direction={orderBy === 'status' ? order : 'asc'}
                onClick={createSortHandler('status')}
              >
                Status
              </TableSortLabel>
            </TableCell>
            <TableCell sx={{ fontWeight: 'bold' }}>
              <TableSortLabel
                active={orderBy === 'category'}
                direction={orderBy === 'category' ? order : 'asc'}
                onClick={createSortHandler('category')}
              >
                Category
              </TableSortLabel>
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
              <TableSortLabel
                active={orderBy === 'hits'}
                direction={orderBy === 'hits' ? order : 'asc'}
                onClick={createSortHandler('hits')}
              >
                Hits
              </TableSortLabel>
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
              <TableSortLabel
                active={orderBy === 'monetized'}
                direction={orderBy === 'monetized' ? order : 'asc'}
                onClick={createSortHandler('monetized')}
              >
                Monetized
              </TableSortLabel>
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
              <TableSortLabel
                active={orderBy === 'clicks'}
                direction={orderBy === 'clicks' ? order : 'asc'}
                onClick={createSortHandler('clicks')}
              >
                Clicks
              </TableSortLabel>
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
              <TableSortLabel
                active={orderBy === 'coverage'}
                direction={orderBy === 'coverage' ? order : 'asc'}
                onClick={createSortHandler('coverage')}
              >
                Coverage (%)
              </TableSortLabel>
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
              <TableSortLabel
                active={orderBy === 'revenue'}
                direction={orderBy === 'revenue' ? order : 'asc'}
                onClick={createSortHandler('revenue')}
              >
                Revenue ($)
              </TableSortLabel>
            </TableCell>
            <TableCell align="right" sx={{ fontWeight: 'bold' }}>
              <TableSortLabel
                active={orderBy === 'payout'}
                direction={orderBy === 'payout' ? order : 'asc'}
                onClick={createSortHandler('payout')}
              >
                Payout ($/click)
              </TableSortLabel>
            </TableCell>
            <TableCell align="center" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {sortedKeywords.length > 0 ? (
            sortedKeywords.map((keyword) => {
              // Get stats for this keyword
              const stats = getKeywordStats(keyword);
              
              return (
                <TableRow key={keyword.id}>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                      {keyword.keyword}
                    </Typography>
                  </TableCell>
                  <TableCell>
                    <Chip 
                      label={keyword.status} 
                      size="small" 
                      color={getStatusColor(keyword.status)}
                      sx={{ textTransform: 'capitalize' }}
                    />
                  </TableCell>
                  <TableCell>
                    {keyword.category ? (
                      <Chip
                        icon={getCategoryIcon(keyword.category)}
                        label={keyword.category.replace('_', ' ')}
                        size="small"
                        variant="outlined"
                        sx={{ 
                          textTransform: 'capitalize',
                          borderColor: 'rgba(0, 0, 0, 0.23)'
                        }}
                      />
                    ) : (
                      <Typography variant="body2" color="text.secondary">
                        —
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell align="right">{stats.hits.toLocaleString()}</TableCell>
                  <TableCell align="right">{stats.monetized.toLocaleString()}</TableCell>
                  <TableCell align="right">{stats.clicks.toLocaleString()}</TableCell>
                  <TableCell align="right">{stats.coverage}%</TableCell>
                  <TableCell align="right">${stats.revenue.toFixed(2)}</TableCell>
                  <TableCell align="right">
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                      <Typography 
                        variant="body2" 
                        sx={{ 
                          fontWeight: 'bold', 
                          color: getPayoutColor(stats.payout),
                          mr: 1
                        }}
                      >
                        ${stats.payout.toFixed(3)}
                      </Typography>
                      {stats.payout > 0.25 ? (
                        <TrendingUpIcon fontSize="small" sx={{ color: getPayoutColor(stats.payout) }} />
                      ) : (
                        <TrendingDownIcon fontSize="small" sx={{ color: getPayoutColor(stats.payout) }} />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell align="center">
                    <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                      <Tooltip title="View Stats">
                        <IconButton 
                          size="small" 
                          onClick={() => onViewStats(keyword)}
                          sx={{ mr: 1 }}
                        >
                          <VisibilityIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title={keyword.status === 'active' ? 'Pause' : 'Activate'}>
                        <IconButton 
                          size="small" 
                          onClick={() => onToggleStatus(keyword.id, keyword.status)}
                          sx={{ mr: 1 }}
                          color={keyword.status === 'active' ? 'warning' : 'success'}
                        >
                          {keyword.status === 'active' ? 
                            <PauseIcon fontSize="small" /> : 
                            <PlayArrowIcon fontSize="small" />
                          }
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Delete">
                        <IconButton 
                          size="small" 
                          color="error"
                          onClick={() => onDeleteKeyword(keyword.id)}
                        >
                          <DeleteIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })
          ) : (
            <TableRow>
              <TableCell colSpan={10} align="center" sx={{ py: 4 }}>
                <Typography variant="body1" color="text.secondary">
                  No keywords found. Add some keywords to get started.
                </Typography>
              </TableCell>
            </TableRow>
          )}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default KeywordTable;